import { CognitoError } from "./cognito";

/**
 * Cognito `__type` → the words a user reads. Kept apart from the transport so
 * both stay small, and so the enumeration rule below is testable: Cognito
 * distinguishes a wrong password from an unknown email, and the UI must not, or
 * the sign-in form becomes an account-existence oracle.
 */
export interface AuthError {
  message: string;
  /** Where the flow should continue, when the failure implies a next step. */
  step?: "verify";
}

const SIGN_IN_FAILED = "We couldn't sign you in. Check your email and password.";
const THROTTLED = "Too many attempts. Wait a moment and try again.";
const GENERIC = "Something went wrong. Try again.";

const MESSAGES: Record<string, AuthError> = {
  NotAuthorizedException: { message: SIGN_IN_FAILED },
  UserNotFoundException: { message: SIGN_IN_FAILED },
  UserNotConfirmedException: {
    message: "Your email isn't verified yet. Enter the code we sent you.",
    step: "verify",
  },
  UsernameExistsException: { message: "That email already has an account. Try signing in." },
  InvalidPasswordException: { message: "That password doesn't meet the requirements below." },
  InvalidParameterException: { message: "Check the details you entered and try again." },
  CodeMismatchException: { message: "That code isn't right. Check it and try again." },
  ExpiredCodeException: { message: "That code has expired. Send yourself a new one." },
  CodeDeliveryFailureException: {
    message: "We couldn't send that email. Check the address and try again.",
  },
  LimitExceededException: { message: THROTTLED },
  TooManyRequestsException: { message: THROTTLED },
  TooManyFailedAttemptsException: { message: THROTTLED },
  PasswordResetRequiredException: {
    message: "Your password needs resetting. Use 'Forgot password?' below.",
  },
  UserLambdaValidationException: { message: GENERIC },
};

export function authErrorFor(type: string | undefined): AuthError {
  return (type && MESSAGES[type]) || { message: GENERIC };
}

/** What to show for anything thrown out of a `lib/cognito` call. */
export function messageFor(e: unknown): AuthError {
  return authErrorFor(e instanceof CognitoError ? e.type : undefined);
}
